import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Palette,
  Stack,
  Typography,
  keyframes,
} from "@mui/material";
import { ChevronDown, Loader2, Mic, Paperclip, Send, Settings2 } from "lucide-react";
import { useRef, useState } from "react";
import {
  FILE_INPUT_ACCEPT,
  PendingUpload,
} from "../../hooks/useFileUpload";
import { DocumentPreviewModal } from "../document/DocumentPreviewModal";
import { borderGeneralStyle } from "@utils/cssStyles";
import { MAX_ATTACHMENTS, MAX_CHAR_LIMIT, MODEL_OPTIONS } from "../../utils/constants";
import { AskDostDictationIndicator } from "../dictator/AskDostDictationIndicator";
import { AttachmentChip } from "@pages/askDost/utils/AttachmentChip";
import { AskDostPreferencesMenu, TAnswerMode } from "../preferences/AskDostPreferencesMenu";
import { AskDostModelMenu } from "../model/AskDostModelMenu";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

interface IFileUploadProps {
  palette: Palette;
  translate: (text: string, options?: any) => string;
  disabled?: boolean;
  toggleListening: () => void;
  onConfirmRecording: () => void;
  onCancelRecording: () => void;
  liveTranscript: string;
  isPaused: boolean;
  onPauseToggle: () => void;
  isSending: boolean;
  isListening: boolean;
  value: string;
  onSubmit: () => void;
  onCancel?: () => void;
  pendingUploads: PendingUpload[];
  isUploading: boolean;
  uploadFiles: (files: FileList | File[]) => Promise<void>;
  removePendingUpload: (pendingId: string) => void;
  onAttachmentClick?: () => void;
  answerMode: TAnswerMode;
  onModeChange: (mode: TAnswerMode) => void;
  selectedModelId: string;
  onModelChange: (modelId: string) => void;
  children?: React.ReactNode;
}


export const FileUpload: React.FC<IFileUploadProps> = (props) => {
  const {
    palette,
    translate,
    disabled = false,
    children,
    toggleListening,
    onConfirmRecording,
    onCancelRecording,
    liveTranscript,
    isPaused,
    onPauseToggle,
    isSending,
    isListening,
    value,
    onSubmit,
    onCancel,
    pendingUploads,
    isUploading,
    uploadFiles,
    removePendingUpload,
    onAttachmentClick,
    answerMode,
    onModeChange,
    selectedModelId,
    onModelChange,
  } = props;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [prefsAnchorEl, setPrefsAnchorEl] = useState<HTMLElement | null>(null);
  const [modelAnchorEl, setModelAnchorEl] = useState<HTMLElement | null>(null);
  const [previewDoc, setPreviewDoc] = useState<{
    handle: string;
    name: string;
  } | null>(null);

  const currentLength = value.length;
  const isOverLimit = currentLength > MAX_CHAR_LIMIT;

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      await uploadFiles(files);
    }
    event.target.value = "";
  };

  const isButtonDisabled = isOverLimit && !isSending;
  const sendIconColor = isButtonDisabled
    ? palette.action.disabled
    : palette.primary.contrastText;
  const selectedModel =
    MODEL_OPTIONS.find((m) => m.id === selectedModelId) || MODEL_OPTIONS[0];

  return (
    <Stack spacing={1} sx={{ flex: 1, width: "100%", minWidth: 0 }}>
      {pendingUploads.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {pendingUploads.map((upload) => (
            <AttachmentChip
              key={upload.id}
              name={upload.file.name}
              palette={palette}
              isLoading={upload.isUploading}
              error={upload.error}
              onRemove={() => removePendingUpload(upload.id)}
              onClick={
                upload.attachment
                  ? () =>
                      setPreviewDoc({
                        handle: upload.attachment!.handle,
                        name: upload.attachment!.name,
                      })
                  : undefined
              }
            />
          ))}
        </Stack>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_INPUT_ACCEPT}
        multiple
        
        hidden
        onChange={handleFileSelected}
      />

      {isListening ? (
        <AskDostDictationIndicator
          palette={palette}
          translate={translate}
          onConfirm={onConfirmRecording}
          onCancel={onCancelRecording}
          liveTranscript={liveTranscript}
          isPaused={isPaused}
          onPauseToggle={onPauseToggle}
        />
      ) : (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={() => {
                if (onAttachmentClick) {
                  onAttachmentClick();
                } else {
                  fileInputRef.current?.click();
                }
              }}
              disabled={
                disabled ||
                isUploading ||
                pendingUploads.length >= MAX_ATTACHMENTS
              }
              title={translate("attach_file")}
              sx={{
                ...borderGeneralStyle,
                width: 36,
                height: 36,
                color: palette.primary.dark,
              }}
            >
              {isUploading ? (
                <CircularProgress size={18} />
              ) : (
                <Paperclip size={18} />
              )}
            </IconButton>

            <IconButton
              size="small"
              onClick={(e) => setPrefsAnchorEl(e.currentTarget)}
              disabled={disabled}
              title={translate("preferences")}
              sx={{
                ...borderGeneralStyle,
                width: 36,
                height: 36,
                color: palette.primary.dark,
              }}
            >
              <Settings2 size={18} />
            </IconButton>
            <AskDostPreferencesMenu
              anchorEl={prefsAnchorEl}
              onClose={() => setPrefsAnchorEl(null)}
              palette={palette}
              translate={translate}
              mode={answerMode}
              onModeChange={onModeChange}
            />
          </Stack>

          <Box sx={{ flex: 1 }}>{children}</Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {isOverLimit && (
              <Typography
                variant="caption"
                sx={{
                  color: palette.error.main,
                  fontWeight: 500,
                  display: { xs: "none", sm: "block" },
                }}
              >
                {currentLength.toLocaleString()}/
                {MAX_CHAR_LIMIT.toLocaleString()}
              </Typography>
            )}

            <Button
              size="small"
              onClick={(e) => setModelAnchorEl(e.currentTarget)}
              endIcon={<ChevronDown size={14} />}
              sx={{
                ...borderGeneralStyle,
                borderRadius: 999,
                color: palette.text.primary,
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                minWidth: 0,
                "&:hover": { backgroundColor: palette.action.hover },
              }}
            >
              {translate(selectedModel.nameKey)}
            </Button>
            <AskDostModelMenu
              anchorEl={modelAnchorEl}
              onClose={() => setModelAnchorEl(null)}
              palette={palette}
              translate={translate}
              selectedModelId={selectedModelId}
              onSelect={onModelChange}
            />

            {isSending ? (
              <IconButton
                size="small"
                onClick={onCancel}
                title={translate("cancel")}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: palette.primary.main,
                  "&:hover": { backgroundColor: palette.primary.dark },
                }}
              >
                <Box
                  component={Loader2}
                  size={16}
                  sx={{
                    color: palette.primary.contrastText || "#ffffff",
                    animation: `${spin} 900ms linear infinite`,
                    display: "inline-flex",
                  }}
                />
              </IconButton>
            ) : value ? (
              <IconButton
                size="small"
                onClick={() => {
                  if (isUploading || isOverLimit) return;
                  onSubmit();
                }}
                title={translate("send_message")}
                disabled={isButtonDisabled}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: palette.primary.main,
                  "&:hover": { backgroundColor: palette.primary.dark },
                  "&.Mui-disabled": {
                    backgroundColor: palette.action.disabledBackground,
                    cursor: "not-allowed",
                    pointerEvents: "auto",
                  },
                }}
              >
                <Send size={16} color={sendIconColor} />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={toggleListening}
                disabled={disabled}
                title={translate("dictate")}
                sx={{
                  width: 36,
                  height: 36,
                  color: palette.text.primary,
                  "&:hover": { backgroundColor: palette.action.hover },
                }}
              >
                <Mic size={18} />
              </IconButton>
            )}
          </Stack>
        </Stack>
      )}

      <DocumentPreviewModal
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        palette={palette}
      />
    </Stack>
  );
};

